import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, API } from "../lib/api";
import { DOC_TYPES } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Verification() {
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("pending");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get(`/verifications?status=${tab}`).then((r) => setDocs(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tab]);

  const review = async (docId, action) => {
    try {
      await api.post(`/documents/${docId}/review`, { action, reason });
      toast.success(`Document ${action === "approve" ? "approved" : "rejected"}`);
      setRejecting(null);
      setReason("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="verification-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">Verification Queue</h1>
        <p className="text-sm text-[#475569] mt-1">Review business verification documents</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E2E8F0]">
          <TabsTrigger value="pending" data-testid="tab-pending-docs">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved-docs">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected-docs">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3" data-testid="documents-queue">
        {docs.length === 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center text-[#94A3B8]" data-testid="queue-empty">
            No {tab} documents.
          </div>
        )}
        {docs.map((d) => (
          <div key={d.id} data-testid={`queue-doc-${d.id}`} className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-xl bg-[#2E7BD6]/10 flex items-center justify-center shrink-0">
                <FileText size={20} className="text-[#2E7BD6]" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <a href={`${API}/files/${d.storage_path}`} target="_blank" rel="noreferrer" data-testid={`queue-doc-link-${d.id}`} className="font-semibold text-[#2E7BD6] hover:underline">
                  {d.original_filename}
                </a>
                <div className="text-sm text-[#475569]">
                  {DOC_TYPES.find((t) => t.value === d.document_type)?.label || d.document_type} · uploaded by {d.uploaded_by} · {new Date(d.created_at).toLocaleString()}
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <Link to={`/admin/listings/${d.business_id}`} data-testid={`queue-biz-link-${d.id}`} className="text-sm font-medium text-[#1B2A4A] hover:text-[#2E7BD6]">
                    {d.business_name}
                  </Link>
                  {d.business_status && <StatusBadge status={d.business_status} />}
                </div>
                {d.status === "rejected" && d.rejection_reason && (
                  <div className="text-sm text-red-600 mt-1.5">Reason: {d.rejection_reason}</div>
                )}
                {d.status !== "pending" && d.reviewed_by && (
                  <div className="text-xs text-[#94A3B8] mt-1">Reviewed by {d.reviewed_by}</div>
                )}
              </div>
              {d.status === "pending" && (
                <div className="flex gap-2">
                  <Button data-testid={`approve-doc-${d.id}`} onClick={() => review(d.id, "approve")} className="bg-[#059669] hover:bg-[#047857] text-white">
                    <Check size={15} className="mr-1.5" /> Approve
                  </Button>
                  <Button data-testid={`reject-doc-${d.id}`} variant="outline" onClick={() => setRejecting(rejecting === d.id ? null : d.id)} className="border-[#DC2626] text-[#DC2626] hover:bg-red-50">
                    <X size={15} className="mr-1.5" /> Reject
                  </Button>
                </div>
              )}
            </div>
            {rejecting === d.id && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-[#F1F5F9]">
                <Input data-testid={`reject-reason-${d.id}`} placeholder="Rejection reason..." value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1" />
                <Button data-testid={`confirm-reject-${d.id}`} onClick={() => review(d.id, "reject")} className="bg-[#DC2626] hover:bg-[#b91c1c] text-white">Confirm Reject</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
